import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
    ClientAddress,
    Contractor,
    ContractorAddress,
    CountriesService,
    Country,
    PersonsService,
    SalesDocumentClient,
} from '@icc/b2b/shared';
import { TranslateService } from '@icc/shared';
import { DialogRef, DIALOG_DATA } from '@icc/ui';
import Fuse from 'fuse.js';
import { Subscription } from 'rxjs';

@Component({
    selector: 'icc-client-dialog',
    templateUrl: './client-dialog.component.html',
    styleUrls: ['./client-dialog.component.scss'],
})
export class ClientDialogComponent implements OnInit, OnDestroy {
    searchWord = '';
    searchNip = '';
    searchAddress = '';
    searchCombined = '';
    existing = this.data.client?.id != null;
    salesDocumentSellerCompanyLicense = this.data?.salesDocumentSellerCompanyLicense ? true : false;
    showExistingContractorForm = false;
    mobileView = false;


    contractorForm = this.fb.group({
        isCompany: false,
        firstName: '',
        lastName: '',
        reference: '',
        email: '',
        phoneNumber: '',
        companyName: '',
        companyVatNr: '',
        companyVatNrEu: '',
        delivery: this.fb.group({
            countryId: 'PL',
            regionId: null,
            subregionId: null,
            addressLine1: '',
            addressLine2: '',
            postalCode: '',
            city: '',
        }),
        invoiceFromDelivery: true,
        invoice: this.fb.group({
            countryId: 'PL',
            regionId: null,
            subregionId: null,
            addressLine1: '',
            addressLine2: '',
            postalCode: '',
            city: '',
        }),
        otherContact: null,
        billingDelivery: this.fb.group({
            countryId: 'PL',
            regionId: null,
            subregionId: null,
            addressLine1: '',
            addressLine2: '',
            postalCode: '',
            city: '',
        }),
    });

    existingContractorForm = this.fb.group({
        id: 0,
        departmentId: 0,
        workerId: '',
        deliveryId: '',
        delivery: this.fb.group({
            countryId: 'PL',
            regionId: null,
            subregionId: null,
            addressLine1: '',
            addressLine2: '',
            postalCode: '',
            city: '',
        }),
    });
    allContractors: Contractor[] = [];
    contractors: Contractor[] = [];
    countries: Country[] = [];
    regions: Country[] = [];
    subregions: Country[] = [];

    selectedContractor: Contractor | null = null;

    @Input() recordsOnPage = 25;
    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() pageEmitter = new EventEmitter();

    private subscriptions: Subscription[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            client: SalesDocumentClient;
            resourceId: number;
            userId: number;
            salesDocumentSellerCompanyLicense?: boolean;
            existing: boolean;
            isServiceOrder?: boolean;
        },
        private translateService: TranslateService,
        private countriesService: CountriesService,
        private personsService: PersonsService,
        private breakpointObserver: BreakpointObserver
    ) {
        this.existing = this.data.existing;
        this.loadContractors();
    }

    async loadContractors() {
        await this.personsService.loaded;
        this.allContractors = this.personsService.getContractors();
        this.contractors = this.allContractors;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.breakpointObserver.observe('(max-width: 550px) or (max-height: 600px)').subscribe((result) => {
                this.mobileView = result.matches;

                if (this.mobileView) {
                    this.searchAddress = '';
                    this.searchNip = '';
                    this.searchWord = '';
                } else {
                    this.searchCombined = '';
                }
            })
        );
        if (this.existingContractorForm.get('deliveryId')) {
            this.subscriptions.push(
                this.existingContractorForm.get('deliveryId').valueChanges.subscribe((value) => {
                    if (+ value) {
                        const address = (
                            this.getDeliveryAddresses(
                                this.existingContractorForm.get('departmentId')?.value
                            ) ?? []
                        ).find((a) => a.id === + value);
                        this.existingContractorForm.patchValue({
                            delivery: address,
                        });
                        this.existingContractorForm.get('delivery')?.disable();
                    } else {
                        this.existingContractorForm.get('delivery')?.enable();
                    }
                })
            );
        }
        if (this.existingContractorForm.get('departmentId')) {
            this.subscriptions.push(
                this.existingContractorForm.get('departmentId').valueChanges.subscribe((value) => {
                    const deliveryId = (this.getDeliveryAddresses(+value ?? 0) ?? [])[0]?.id;
                    this.existingContractorForm.patchValue({
                        deliveryId,
                    });
                })
            );
        }

        if (!this.data.client?.id) {
            this.editClientInForm();
        } else {
            this.personsService.loaded.then(() => {
                this.selectedContractor = this.contractors?.find(
                    (c) => c.id === this.data.client?.id
                );
                this.contractorForm.get('invoice')?.disable();
                this.editContractorInForm();
                this.existing = true;
                this.showExistingContractorForm = true;
            });
        }
        this.countries = this.countriesService.getCountries();

        if (this.contractorForm.get('invoiceFromDelivery')) {
            this.subscriptions.push(
                this.contractorForm.get('invoiceFromDelivery').valueChanges.subscribe((value) => {
                    if (value) {
                        this.contractorForm.patchValue({
                            invoice: this.contractorForm.value.delivery,
                        });
                        this.contractorForm.get('invoice')?.disable();
                    } else {
                        this.contractorForm.get('invoice')?.enable();
                    }
                }),
                this.contractorForm.get('delivery').valueChanges.subscribe((value) => {
                    if (this.contractorForm.get('invoiceFromDelivery')?.value) {
                        this.contractorForm.patchValue({
                            invoice: value,
                        });
                    }
                    if (!this.contractorForm.get('otherContact')?.value) {
                        this.contractorForm.patchValue({
                            billingDelivery: value,
                        });
                    }
                }),
            );
        }

        if (this.contractorForm.get('otherContact')) {
            this.subscriptions.push(
                this.contractorForm.get('otherContact').valueChanges.subscribe((value) => {
                    if (!value) {
                        this.contractorForm.patchValue({
                            billingDelivery: this.contractorForm.value.delivery,
                        });
                    }
                }),
            );
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    add() {
        if (this.contractorForm.value && this.contractorForm.valid) {
            if (this.contractorForm.value.invoiceFromDelivery) {
                this.contractorForm.value.invoice = this.contractorForm.value.delivery;
            }
             if (!this.contractorForm.value.otherContact || !this.contractorForm.value.billingDelivery) {
                this.contractorForm.value.billingDelivery = this.contractorForm.value.delivery;
            }
            this.dialogRef.close(this.contractorForm.value);
        }
    }

    async handleSearch() {
        if(this.searchWord || this.searchNip || this.searchAddress || this.searchCombined){

            let contractors = this.allContractors ?? [];

            if (this.searchCombined) {
                contractors = contractors.filter((contractor) => {
                    return (
                        this.handleSearchWord(this.searchCombined, [contractor]).length > 0 ||
                        this.handleSearchNip(this.searchCombined, [contractor]).length > 0 ||
                        this.handleSearchAddress(this.searchCombined, [contractor]).length > 0
                    );
                });
            } else {
                if (this.searchWord) {
                    contractors = this.handleSearchWord(this.searchWord, contractors );
                }
                if (this.searchNip) {
                    contractors = this.handleSearchNip(this.searchNip, contractors);
                }
                if (this.searchAddress) {
                    contractors =this.handleSearchAddress(this.searchAddress, contractors);
                }
            }

            this.contractors = contractors;

        }else{
            this.contractors = this.allContractors ?? [];
        }
    }

    handleSearchWord(searchWord:string,contractors:Contractor[]):Contractor[] {

        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['name','email','phone','person.firstName','person.lastName'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchWord);
        return result;
    }

    handleSearchNip(searchNip:string,contractors:Contractor[]):Contractor[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['company.vatNr','company.vatNrEu'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchNip);
        return result;
    }

    handleSearchAddress(searchAddress:string,contractors:Contractor[]):Contractor[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['registry.addressLine1','registry.addressLine2','registry.city','registry.postalCode'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchAddress);
        return result;
    }

    getShortName(contractor:Contractor):string{
        if(contractor?.company?.shortName){
            return contractor.company.shortName;
        }
        return '';
    }

    getLocalizationTextFromAddress(address:ContractorAddress):string{
        let localization = '';
        if(address?.countryId){
            localization = address.countryId;
        }
        if(address?.city){
            if(localization) localization += ', ';
            localization += address.city;
        }
        if(address?.postalCode){
            if(localization) localization += ', ';
            localization += address.postalCode;
        }
        return localization;
    }

    getContractorLocalization(contractor:Contractor):string{
        let localization = '';
        if(contractor?.registry){
            localization = this.getLocalizationTextFromAddress(contractor.registry);
        }else if(contractor?.contactAddresses?.length){
            localization = this.getLocalizationTextFromAddress(contractor.contactAddresses[0]);
        }else if(contractor?.deliveryAddresses?.length){
            localization = this.getLocalizationTextFromAddress(contractor.deliveryAddresses[0]);
        }else if(contractor?.delivery){
            localization = this.getLocalizationTextFromAddress(contractor.delivery);
        }
        return localization;
    }

    getContractorEmailPhone(contractor:Contractor):string{
        let emailPhone = '';
        if(contractor?.email){
            emailPhone = contractor?.email;
        }
        if(contractor?.phoneNumber){
            if(emailPhone) emailPhone += ', ';
            emailPhone += contractor.phoneNumber;
        }
        return emailPhone;
    }

    select(contractorId: number) {
        this.showExistingContractorForm = true;
        const changed = contractorId !== this.selectedContractor?.id;
        this.selectedContractor = this.contractors?.find((c) => c.id === contractorId);
        this.editContractorInForm(changed);
    }

    saveExisting() {
        if (this.existingContractorForm.value && this.existingContractorForm.valid) {
            if(this.existingContractorForm.value.deliveryId){
                this.existingContractorForm.patchValue({
                    deliveryId : + this.existingContractorForm.get('deliveryId').value
                });
            }

            this.dialogRef.close({
                id: this.selectedContractor?.id,
                resourceId: this.existingContractorForm.value.departmentId,
                workerId: Number(this.existingContractorForm.value.workerId),
                person:
                    this.getPersonById(
                        this.existingContractorForm.value.departmentId,
                        Number(this.existingContractorForm.value.workerId)
                    ) ?? this.selectedContractor?.person,
                addresses: {
                    id: this.existingContractorForm.get('id').value,
                    delivery: this.existingContractorForm.get('delivery').value,
                    deliveryId: this.existingContractorForm.get('deliveryId').value,
                }
            });
        }
    }

    close() {
        this.dialogRef.close();
    }

    getDeliveryAddresses(departmentId: number) {
        if (!this.selectedContractor) {
            return [];
        }
        if (departmentId && this.existingContractorForm.get('departmentId')) {
            const department = this.selectedContractor?.company?.resources.find(
                (r) => r.id === +departmentId
            );
            if (department?.deliveryAddresses) {
                return department?.deliveryAddresses;
            } else {
                return [];
            }
        }
        return this.selectedContractor.deliveryAddresses;
    }

    getBillingDeliveryAddresses(departmentId: number) {
        if (!this.selectedContractor) {
            return [];
        }
        if (this.existingContractorForm.get('departmentId')) {
            const department = this.selectedContractor?.company?.resources.find(
                (r) => r.id === +departmentId
            );
            if (department?.contactAddresses) {
                return department?.contactAddresses;
            }
        }
        return this.selectedContractor.contactAddresses;
    }

    editClientInForm() {
        const invoiceFromDelivery = this.equalAddresses(
            this.data.client?.delivery,
            this.data.client?.invoice
        );
        this.contractorForm.patchValue({
            isCompany: this.data.client?.isCompany ?? false,
            firstName: this.data.client?.firstName || '',
            lastName: this.data.client?.lastName || '',
            reference: this.data.client?.reference || '',
            email: this.data.client?.email || '',
            phoneNumber: this.data.client?.phoneNumber || '',
            companyName: this.data.client?.companyName || '',
            companyVatNr: this.data.client?.companyVatNr || '',
            companyVatNrEu: this.data.client?.companyVatNrEu || '',
            invoiceFromDelivery,
            delivery: {
                countryId: this.data.client?.delivery?.countryId || 'PL',
                regionId: this.data.client?.delivery?.regionId || null,
                subregionId: this.data.client?.delivery?.subregionId || null,
                addressLine1: this.data.client?.delivery?.addressLine1 || '',
                addressLine2: this.data.client?.delivery?.addressLine2 || '',
                postalCode: this.data.client?.delivery?.postalCode || '',
                city: this.data.client?.delivery?.city || '',
            },
            invoice: {
                countryId: this.data.client?.invoice?.countryId || 'PL',
                regionId: this.data.client?.invoice?.regionId || null,
                subregionId: this.data.client?.invoice?.subregionId || null,
                addressLine1: this.data.client?.invoice?.addressLine1 || '',
                addressLine2: this.data.client?.invoice?.addressLine2 || '',
                postalCode: this.data.client?.invoice?.postalCode || '',
                city: this.data.client?.invoice?.city || '',
            },
            otherContact:
                this.data.client?.billingDelivery != null &&
                Boolean(this.data.client?.billingDelivery),
            billingDelivery: {
                countryId: this.data.client?.billingDelivery?.countryId || 'PL',
                regionId: this.data.client?.billingDelivery?.regionId || null,
                subregionId: this.data.client?.billingDelivery?.subregionId || null,
                addressLine1: this.data.client?.billingDelivery?.addressLine1 || '',
                addressLine2: this.data.client?.billingDelivery?.addressLine2 || '',
                postalCode: this.data.client?.billingDelivery?.postalCode || '',
                city: this.data.client?.billingDelivery?.city || '',
            },
        });
        if (invoiceFromDelivery) {
            this.contractorForm.get('invoice')?.disable();
        }
    }

    private equalAddresses(address1: ClientAddress, address2: ClientAddress) {
        return (
            address1?.countryId === address2?.countryId &&
            address1?.countryId === address2?.countryId &&
            address1?.regionId === address2?.regionId &&
            address1?.subregionId === address2?.subregionId &&
            address1?.addressLine1 === address2?.addressLine1 &&
            address1?.addressLine2 === address2?.addressLine2 &&
            address1?.postalCode === address2?.postalCode &&
            address1?.city === address2?.city
        );
    }

    editContractorInForm(changed = false) {
        const workerPerson =
            this.getPersonByUserId(this.data.resourceId ?? 0, this.data.userId) ??
            this.selectedContractor?.person;
        const department = this.selectedContractor?.company?.resources?.find(
            (r) => r.id === +this.data.resourceId
        );
        const deliveryAddresses = this.getDeliveryAddresses(department?.id) ?? [];
        const deliveryId =
            !changed && this.data.client?.deliveryId === 0
                ? this.data.client?.deliveryId
                : deliveryAddresses.find((a) => a.id === this.data.client?.deliveryId)?.id ??
                  +deliveryAddresses[0]?.id;
        this.existingContractorForm.patchValue({
            id: this.selectedContractor?.id,
            departmentId: department?.id ?? 0,
            workerId: workerPerson?.id,
            delivery: {
                countryId: this.data.client?.delivery?.countryId || 'PL',
                regionId: this.data.client?.delivery?.regionId || null,
                subregionId: this.data.client?.delivery?.subregionId || null,
                addressLine1: this.data.client?.delivery?.addressLine1 || '',
                addressLine2: this.data.client?.delivery?.addressLine2 || '',
                postalCode: this.data.client?.delivery?.postalCode || '',
                city: this.data.client?.delivery?.city || '',
            },
            deliveryId,
        });
    }

    getPersonById(resourceId: number, id: number) {
        if (this.selectedContractor?.company?.id && id) {
            return (this.selectedContractor?.company?.workers[resourceId ?? 0] || []).find(
                (p) => p.id === +id
            );
        }
        return null;
    }

    getPersonByUserId(resourceId: number, userId: number) {
        if (this.selectedContractor?.company?.id && userId) {
            return (this.selectedContractor?.company?.workers[resourceId ?? 0] || []).find(
                (p) => p.userId === +userId
            );
        }
        return null;
    }

    onChangePage(page: number) {
        this.page = page;
        this.pageEmitter.emit(page);
    }

    showRegions(countryId: string) {
        return this.countriesService.getCountry(countryId)?.showRegions;
    }

    getRegionLabel(countryId: string) {
        return (
            this.countriesService.getCountry(countryId)?.regionLabel ??
            this.translateService.instant('CONTACT|Region')
        );
    }

    showSubregions(countryId: string) {
        return this.countriesService.getCountry(countryId)?.showSubregions;
    }

    getSubregionLabel(countryId: string) {
        return (
            this.countriesService.getCountry(countryId)?.subregionLabel ??
            this.translateService.instant('CONTACT|Powiat')
        );
    }

    getRegions(countryId: string) {
        return this.countriesService.getRegions(countryId).filter((region) => !region.parentId);
    }

    getSubregions(countryId: string, regionId: number) {
        return this.countriesService
            .getRegions(countryId)
            .filter((region) => region.parentId === Number(regionId));
    }

    isRequiredInOrder(){
        return this.salesDocumentSellerCompanyLicense;
    }
}
